import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  catchError,
  filter,
  map,
  Observable,
  of,
  take,
  withLatestFrom,
} from 'rxjs';
import { CreateSimXInstitutionDto } from '../../../shared/models/dtos/create-simx-institution.dto';
import { CreateSimXInstitutionReleaseTagDto } from '../../../shared/models/dtos/create-simx-institution-release-tag.dto';
import { CreateSimXInstitutionScenarioPackageDto } from '../../../shared/models/dtos/create-simx-institution-scenario-package.dto';
import { UpdateSimXInstitutionDto } from '../../../shared/models/dtos/update-simx-institution.dto';
import { UpdateSimXInstitutionReleaseTagDto } from '../../../shared/models/dtos/update-simx-institution-release-tag.dto';
import { UpdateSimXInstitutionScenarioPackageDto } from '../../../shared/models/dtos/update-simx-institution-scenario-package.dto';
import { SimXInstitution } from '../../../shared/models/entities/simx-institution.model';
import { SimXInstitutionReleaseTag } from '../../../shared/models/entities/simx-institution-release-tag.model';
import { SimXInstitutionScenarioPackage } from '../../../shared/models/entities/simx-institution-scenario-package.model';
import { SimXScenarioPackage } from '../../../shared/models/entities/simx-scenario-package.model';
import { SimXUser } from '../../../shared/models/entities/simx-user.model';
import { CreateSimXInstitutionRequest } from '../../../shared/models/requests/create-simx-institution-request.model';
import { GetSimXInstitutionOptions } from '../../../shared/models/requests/entity-options/get-simx-institution-options.model';
import { GetSimXInstitutionReleaseTagOptions } from '../../../shared/models/requests/entity-options/get-simx-institution-release-tag-options.model';
import { GetSimXInstitutionRequest } from '../../../shared/models/requests/get-simx-institution-request.model';
import { GetSimXInstitutionScenarioPackageOptions } from '../../../shared/models/requests/entity-options/get-simx-institution-scenario-package-options.model';
import { GetSimXInstitutionScenarioPackagesRequest } from '../../../shared/models/requests/get-simx-institution-scenario-packages-request.model';
import { GetSimXScenarioPackageRequest } from '../../../shared/models/requests/get-simx-scenario-package-request.model';
import { UpdateSimXInstitutionRequest } from '../../../shared/models/requests/update-simx-institution-request.model';
import { InstitutionDataService } from '../../../shared/services/institution-data.service';
import { InstitutionReleaseTagDataService } from '../../../shared/services/institution-release-tag-data.service';
import { InstitutionScenarioPackageDataService } from '../../../shared/services/institution-scenario-package-data.service';
import { ScenarioPackageDataService } from '../../../shared/services/scenario-package-data.service';
import { AuthenticationService } from '../../authentication/services/authentication.service';
import { Permission } from '../../authorization/definitions/permission.enum';
import { AuthorizationService } from '../../authorization/services/authorization.service';
import { Role } from '../../role/definitions/role.enum';
import { SimXInstitutionEntityAttribute } from '../definitions/simx-institution-entity-attribute.enum';
import * as InstitutionStoreActions from '../store/institution-store.actions';
import * as InstitutionStoreSelectors from '../store/institution-store.selectors';

@Injectable({ providedIn: 'root' })
export class InstitutionAdministrationService {
  private readonly _authenticationService = inject(AuthenticationService);
  private readonly _authorizationService = inject(AuthorizationService);
  private readonly _institutionDataService = inject(InstitutionDataService);
  private readonly _institutionReleaseTagDataService = inject(
    InstitutionReleaseTagDataService,
  );
  private readonly _institutionScenarioPackageDataService = inject(
    InstitutionScenarioPackageDataService,
  );
  private readonly _scenarioPackageDataService = inject(
    ScenarioPackageDataService,
  );
  private readonly _store = inject(Store);

  scenarioPackageListStore: Array<SimXScenarioPackage> = null;
  scenarioPackageWithItemsStore: Map<string, SimXScenarioPackage> = new Map<
    string,
    SimXScenarioPackage
  >();

  institutionExpirationWindowMax: number = 30;
  urgentInstitutionExpirationWindowMax: number = 15;

  constructor() {
    this.resetLocalStores();

    this._authenticationService.getIsLoggedIn$().subscribe((state: boolean) => {
      if (!state) {
        this.resetLocalStores();
      }
    });
  }

  resetLocalStores(): void {
    this.scenarioPackageListStore = null;
    this.scenarioPackageWithItemsStore.clear();
  }

  authenticatedUserCan$(permission: Permission): Observable<boolean> {
    return this._authorizationService.authenticatedUserCan(permission);
  }

  authenticatedUserCanEditInstitutions$(): Observable<boolean> {
    return this.authenticatedUserCan$(Permission.EditInstitutions);
  }

  authenticatedUserCanView$(
    institutionEntityAttribute: SimXInstitutionEntityAttribute,
  ): Observable<boolean> {
    return this._authenticationService.getUser$().pipe(
      filter((user): user is SimXUser => !!user?.role?.name),
      take(1),
      map(user => {
        const allowedUserRoles: Array<Role> = [];
        switch (institutionEntityAttribute) {
          case SimXInstitutionEntityAttribute.Active: {
            allowedUserRoles.push(...Object.values(Role));
            break;
          }
          case SimXInstitutionEntityAttribute.Code: {
            allowedUserRoles.push(...Object.values(Role));
            break;
          }
          case SimXInstitutionEntityAttribute.CreatedAt: {
            allowedUserRoles.push(
              Role.Partner,
              Role.SimXUser,
              Role.SimXEditor,
              Role.SimXSales,
              Role.SimXAdministrator,
              Role.SimXSuperAdministrator,
            );
            break;
          }
          case SimXInstitutionEntityAttribute.Expiration: {
            allowedUserRoles.push(
              Role.Educator,
              Role.Administrator,
              Role.Partner,
              Role.SimXUser,
              Role.SimXEditor,
              Role.SimXSales,
              Role.SimXAdministrator,
              Role.SimXSuperAdministrator,
            );
            break;
          }
          case SimXInstitutionEntityAttribute.Id: {
            allowedUserRoles.push(...Object.values(Role));
            break;
          }
          case SimXInstitutionEntityAttribute.InstitutionReleaseTags: {
            allowedUserRoles.push(
              Role.Partner,
              Role.SimXSales,
              Role.SimXAdministrator,
              Role.SimXSuperAdministrator,
            );
            break;
          }
          case SimXInstitutionEntityAttribute.InstitutionScenarioPackages: {
            allowedUserRoles.push(
              Role.Partner,
              Role.SimXUser,
              Role.SimXEditor,
              Role.SimXSales,
              Role.SimXAdministrator,
              Role.SimXSuperAdministrator,
            );
            break;
          }
          case SimXInstitutionEntityAttribute.InstitutionUsers: {
            allowedUserRoles.push(...Object.values(Role));
            break;
          }
          case SimXInstitutionEntityAttribute.Name: {
            allowedUserRoles.push(...Object.values(Role));
            break;
          }
          case SimXInstitutionEntityAttribute.ReportingCodes: {
            allowedUserRoles.push(...Object.values(Role));
            break;
          }
          case SimXInstitutionEntityAttribute.SimSessions: {
            allowedUserRoles.push(...Object.values(Role));
            break;
          }
          case SimXInstitutionEntityAttribute.SimulationOptions: {
            allowedUserRoles.push(
              Role.Partner,
              Role.SimXUser,
              Role.SimXEditor,
              Role.SimXSales,
              Role.SimXAdministrator,
              Role.SimXSuperAdministrator,
            );
            break;
          }
          case SimXInstitutionEntityAttribute.Tags: {
            allowedUserRoles.push(
              Role.Partner,
              Role.SimXUser,
              Role.SimXEditor,
              Role.SimXSales,
              Role.SimXAdministrator,
              Role.SimXSuperAdministrator,
            );
            break;
          }
          case SimXInstitutionEntityAttribute.Type: {
            allowedUserRoles.push(
              Role.Partner,
              Role.SimXUser,
              Role.SimXEditor,
              Role.SimXSales,
              Role.SimXAdministrator,
              Role.SimXSuperAdministrator,
            );
            break;
          }
          case SimXInstitutionEntityAttribute.UpdatedAt: {
            allowedUserRoles.push(...Object.values(Role));
            break;
          }
          default:
        }

        return allowedUserRoles.includes(user?.role?.name as Role);
      }),
    );
  }

  getNewInstitution(): Observable<Partial<SimXInstitution>> {
    return new Observable(observer => {
      const institution: Partial<SimXInstitution> = {
        active: true,
        code: '',
        entityTags: [],
        expiration:
          this.getDefaultInstitutionExpirationDate('demo').toISOString(),
        institutionReleaseTags: [],
        institutionScenarioPackages: [],
        institutionUsers: [],
        name: '',
        simulationOptions: {},
        type: 'demo',
      };
      observer.next(institution);
      observer.complete();
    });
  }

  getInstitution(
    institutionId: string,
    returnOptions: GetSimXInstitutionOptions = {},
  ): Observable<SimXInstitution> {
    const request: GetSimXInstitutionRequest = {
      returnOptions,
    };
    return this._institutionDataService.getInstitution(institutionId, request);
  }

  getInstitutionIsExpired(institution: Partial<SimXInstitution>): boolean {
    const now = new Date();
    return institution.expiration && new Date(institution.expiration) < now;
  }

  getDefaultInstitutionExpirationDate(institutionType: string): Date {
    const now = new Date().setSeconds(0, 0);
    switch (institutionType) {
      case 'demo':
        // Eight weeks from now
        return new Date(new Date(now).setDate(new Date(now).getDate() + 56));
      case 'simple':
        // One year from now
        return new Date(
          new Date(now).setFullYear(new Date(now).getFullYear() + 1),
        );
      default:
        return new Date(now);
    }
  }

  createInstitution(
    data: CreateSimXInstitutionDto,
    returnOptions: GetSimXInstitutionOptions = {},
  ): Observable<SimXInstitution> {
    return new Observable(observer => {
      const request: CreateSimXInstitutionRequest = {
        data,
        returnOptions,
      };
      this._institutionDataService.createInstitution(request).subscribe({
        next: institution => {
          this._store.dispatch(
            InstitutionStoreActions.addInstitutionToInstitutionList({
              institution,
            }),
          );
          observer.next(institution);
          observer.complete();
        },
        error: error => {
          observer.error(error);
        },
      });
    });
  }

  updateInstitution(
    institutionId: string,
    data: UpdateSimXInstitutionDto,
    returnOptions: GetSimXInstitutionOptions = {},
  ): Observable<SimXInstitution> {
    return new Observable(observer => {
      const request: UpdateSimXInstitutionRequest = {
        data,
        returnOptions,
      };
      this._institutionDataService
        .updateInstitution(institutionId, request)
        .subscribe({
          next: institution => {
            this._store.dispatch(
              InstitutionStoreActions.updateInstitutionInInstitutionList({
                institution,
              }),
            );
            observer.next(institution);
            observer.complete();
          },
          error: error => {
            observer.error(error);
          },
        });
    });
  }

  activateInstitution(institutionId: string): Observable<SimXInstitution> {
    return new Observable(observer => {
      this._institutionDataService
        .activateInstitution(institutionId)
        .subscribe({
          next: institution => {
            this._store.dispatch(
              InstitutionStoreActions.updateInstitutionInInstitutionList({
                institution,
              }),
            );
            observer.next(institution);
            observer.complete();
          },
          error: error => {
            observer.error(error);
          },
        });
    });
  }

  setInstitutionReleaseTagsForInstitution(
    institutionId: string,
    institutionReleaseTags: Array<
      CreateSimXInstitutionReleaseTagDto | UpdateSimXInstitutionReleaseTagDto
    >,
    returnOptions: GetSimXInstitutionReleaseTagOptions = {},
  ): Observable<Array<SimXInstitutionReleaseTag>> {
    return new Observable(observer => {
      const request = {
        data: institutionReleaseTags,
        returnOptions,
      };
      this._institutionReleaseTagDataService
        .setInstitutionReleaseTagsForInstitution(institutionId, request)
        .subscribe({
          next: institutionReleaseTags => {
            observer.next(institutionReleaseTags);
            observer.complete();
          },
          error: error => {
            observer.error(error);
          },
        });
    });
  }

  getInstitutionScenarioPackagesForInstitution(
    institutionId: string,
    returnOptions: GetSimXInstitutionScenarioPackageOptions = {},
  ): Observable<Array<SimXInstitutionScenarioPackage>> {
    return new Observable(observer => {
      const request: GetSimXInstitutionScenarioPackagesRequest = {
        returnOptions,
      };
      this._institutionScenarioPackageDataService
        .getInstitutionScenarioPackagesForInstitution(institutionId, request)
        .subscribe({
          next: institutionScenarioPackages => {
            observer.next(institutionScenarioPackages);
            observer.complete();
          },
          error: error => {
            observer.error(error);
          },
        });
    });
  }

  setInstitutionScenarioPackagesForInstitution(
    institutionId: string,
    institutionScenarioPackages: Array<
      | CreateSimXInstitutionScenarioPackageDto
      | UpdateSimXInstitutionScenarioPackageDto
    >,
    returnOptions: GetSimXInstitutionScenarioPackageOptions = {},
  ): Observable<Array<SimXInstitutionScenarioPackage>> {
    return new Observable(observer => {
      const request = {
        data: institutionScenarioPackages,
        returnOptions,
      };
      this._institutionScenarioPackageDataService
        .setInstitutionScenarioPackageItemsForInstitution(
          institutionId,
          request,
        )
        .subscribe({
          next: institutionScenarioPackages => {
            observer.next(institutionScenarioPackages);
            observer.complete();
          },
          error: error => {
            observer.error(error);
          },
        });
    });
  }

  getScenarioPackageWithItems(
    scenarioPackageId: string,
  ): Observable<SimXScenarioPackage> {
    return new Observable(observer => {
      if (this.scenarioPackageWithItemsStore.has(scenarioPackageId)) {
        observer.next(
          this.scenarioPackageWithItemsStore.get(scenarioPackageId),
        );
        observer.complete();
      } else {
        const request: GetSimXScenarioPackageRequest = {
          returnOptions: {
            include: {
              scenarioPackageItems: {
                scenario: {},
              },
            },
          },
        };
        this._scenarioPackageDataService
          .getScenarioPackage(scenarioPackageId, request)
          .subscribe({
            next: scenarioPackage => {
              this.scenarioPackageWithItemsStore.set(
                scenarioPackageId,
                scenarioPackage,
              );
              observer.next(
                this.scenarioPackageWithItemsStore.get(scenarioPackageId),
              );
              observer.complete();
            },
            error: error => {
              observer.error(error);
            },
          });
      }
    });
  }

  fetchInstitutionList() {
    return this._institutionDataService.getInstitutions().pipe(
      map(institutions =>
        InstitutionStoreActions.loadInstitutionListSucceeded({ institutions }),
      ),
      catchError(error =>
        of(InstitutionStoreActions.loadInstitutionListFailed({ error })),
      ),
    );
  }

  getInstitutionList$(): Observable<Array<SimXInstitution>> {
    return new Observable(observer => {
      const subscription = this._store
        .select(InstitutionStoreSelectors.selectInstitutionList)
        .pipe(withLatestFrom(this.getHasLoadedInstitutionList$()))
        .subscribe({
          next: ([institutions, hasLoadedInstitutionList]) => {
            if (hasLoadedInstitutionList) {
              observer.next(institutions);
            }
          },
          error: error => {
            observer.error(error);
          },
        });
      this._store.dispatch(
        InstitutionStoreActions.checkInstitutionListLoaded(),
      );

      return () => subscription.unsubscribe();
    });
  }

  getIsLoadingInstitutionList$(): Observable<boolean> {
    return new Observable(observer => {
      const subscription = this._store
        .select(InstitutionStoreSelectors.selectIsLoadingInstitutionList)
        .subscribe({
          next: state => {
            observer.next(state);
          },
          error: error => {
            observer.error(error);
          },
        });

      return () => subscription.unsubscribe();
    });
  }

  getHasLoadedInstitutionList$(): Observable<boolean> {
    return new Observable(observer => {
      const subscription = this._store
        .select(InstitutionStoreSelectors.selectHasLoadedInstitutionList)
        .subscribe({
          next: state => {
            observer.next(state);
          },
          error: error => {
            observer.error(error);
          },
        });

      return () => subscription.unsubscribe();
    });
  }

  getLoadInstitutionListError$(): Observable<string> {
    return new Observable(observer => {
      const subscription = this._store
        .select(InstitutionStoreSelectors.selectLoadInstitutionListError)
        .subscribe({
          next: error => {
            observer.next(error);
          },
          error: error => {
            observer.error(error);
          },
        });

      return () => subscription.unsubscribe();
    });
  }

  getAuthenticatedUserAllowedScenarioPackages(): Observable<
    Array<SimXScenarioPackage>
  > {
    return new Observable(observer => {
      if (this.scenarioPackageListStore) {
        observer.next([...this.scenarioPackageListStore]);
        observer.complete();
      } else {
        this._scenarioPackageDataService.getScenarioPackages().subscribe({
          next: scenarioPackages => {
            this.scenarioPackageListStore = scenarioPackages;
            observer.next([...this.scenarioPackageListStore]);
            observer.complete();
          },
          error: error => {
            observer.error(error);
          },
        });
      }
    });
  }

  refreshInstitutionList(): void {
    this._store.dispatch(
      InstitutionStoreActions.resetInstitutionStoreAndLoadInstitutionList(),
    );
  }
}
